import React, { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { ResponsiveSankey } from "@nivo/sankey";
import { formatTo2Decimals } from "../grid/columnDefinitions/columnDefinitions";

export const rootNodeBracket = "__Root__";

const AdvancedSankey = ({ 
  data, 
  height = 500, 
  nodeThickness = 12, 
  nodeSpacing = 24, 
  margin = { top: 20, right: 180, bottom: 20, left: 180 },
  valueLabel = ""
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  
  const totalRootValue = useMemo(() => {
    if (!data.links) return 0;
    return data.links
      .filter((l) => l.source && String(l.source).includes(rootNodeBracket))
      .reduce((sum, l) => sum + (l.value || 0), 0);
  }, [data]);

  return (
    <Box sx={{ height: height, width: "100%", minWidth: 900 }}>
      <ResponsiveSankey
        data={data}
        margin={margin}
        align="justify"
        nodeOpacity={1}
        nodeThickness={nodeThickness}
        nodeSpacing={nodeSpacing}
        nodeColor={{ from: "color" }}
        linkColor={(link) => link.color || "rgba(200, 200, 200, 0.3)"}
        linkOpacity={0.5}
        linkBlendMode="multiply"
        enableLinkGradient={false}
        labelPosition="inside"
        labelOrientation="horizontal"
        labelPadding={16}
        labelTextColor={isDark ? "#f8fafc" : "#0f172a"}
        theme={{
          labels: {
            text: { 
              fontSize: 13, 
              fill: isDark ? "#f8fafc" : "#0f172a", 
              fontFamily: "Poppins, sans-serif", 
              fontWeight: 500 
            },
          },
          axis: {
            ticks: {
              text: { 
                fontSize: 11, 
                fill: isDark ? "#a1a1aa" : "#475569", 
                fontFamily: "Poppins, sans-serif" 
              },
            },
          },
          tooltip: {
            container: {
              background: isDark ? "#18181b" : "#ffffff",
              color: isDark ? "#f4f4f5" : "#18181b",
              fontSize: "12px",
              borderRadius: "8px",
              boxShadow: isDark ? "0 10px 25px rgba(0,0,0,0.5)" : "0 10px 25px rgba(0,0,0,0.08)",
              border: `1px solid ${isDark ? "#3f3f46" : "#e2e8f0"}`,
              padding: "10px 14px",
              fontFamily: "Poppins, sans-serif"
            },
          },
        }}
        label="label"
        valueFormat={(value) => formatTo2Decimals(value)}
        linkTooltip={({ link }) => {
          const value = link.value || 0;
          const percentage = totalRootValue > 0 ? ((value / totalRootValue) * 100).toFixed(1) : null;

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Box sx={{ height: 10, width: 10, backgroundColor: link.source.color, borderRadius: "50%" }} />
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#f1f5f9" : "#1e293b" }}>
                  {link.source.label}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: isDark ? "#71717a" : "#94a3b8" }}>➔</Typography>
                <Typography sx={{ fontSize: "12px", fontWeight: 600, color: isDark ? "#f1f5f9" : "#1e293b" }}>
                  {link.target.label}
                </Typography>
                <Box sx={{ height: 10, width: 10, backgroundColor: link.target.color, borderRadius: "50%" }} />
              </Box>
              <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between", mt: 0.5 }}>
                <Typography sx={{ fontSize: "11px", color: isDark ? "#34d399" : "#16a34a", fontWeight: 500 }}>
                  {formatTo2Decimals(value)} {valueLabel}
                </Typography>
                {percentage !== null && percentage !== "0.0" && (
                  <Typography sx={{ fontSize: "11px", color: isDark ? "#a1a1aa" : "#64748b" }}>
                    {percentage}% of total
                  </Typography>
                )}
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
};

export default AdvancedSankey;
